import React from 'react';

import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import { CircularProgress, IconButton, Link, Tooltip, Typography } from '@mui/material';

import { ACTION_BUTTON_SX } from '~/pages/AskRichardUnified/uiStream/AnswerActions';

import {
  type LensAppliedChange,
  lensChangeFailure,
  useLensUndo,
  useLensViewUndo,
} from '~/hooks/apis/reporting/useLensChange';

import type { LeDataContentBlock } from '~/types/leData';

type AnswerChanges = { lensId: string; view: boolean; ids: Array<number>; changed: LensAppliedChange['changed']; said: Array<string> };

function answerChanges(blocks: ReadonlyArray<LeDataContentBlock>): Array<AnswerChanges> {
  const byLens = new Map<string, AnswerChanges>();
  for (const block of blocks) {
    if (block.type !== 'lens_applied') continue;
    const receipt = block.payload as unknown as LensAppliedChange;
    if (!receipt.applied || receipt.undoes) continue;
    const group = byLens.get(receipt.lensId) ?? {
      lensId: receipt.lensId,
      view: Boolean(receipt.view),
      ids: [],
      changed: { cards: [], lens: [], datasets: [] },
      said: [],
    };
    group.said.push(...(receipt.said ?? []));
    group.ids.push(receipt.view ? receipt.view.change : receipt.toVersion);
    for (const card of receipt.changed.cards) {
      if (!group.changed.cards.some((entry) => entry.id === card.id)) group.changed.cards.push(card);
    }
    group.changed.lens = [...new Set([...group.changed.lens, ...receipt.changed.lens])];
    group.changed.datasets = [...new Set([...group.changed.datasets, ...receipt.changed.datasets])];
    byLens.set(receipt.lensId, group);
  }
  return [...byLens.values()];
}

function undoTitle(changes: AnswerChanges) {
  return changes.said.length ? `Undo: ${changes.said.join('; ')}` : `Undo changes to ${changedNames(changes.changed)}`;
}

function changedNames(changed: LensAppliedChange['changed']) {
  const names = [...changed.cards.map((card) => card.title), ...changed.lens.map((field) => `the Lens ${field}`)];
  return names.join(', ') || 'the Lens';
}

function UndoIconButton({ label, title, pending, onClick }: { label: 'Undo' | 'Redo'; title: string; pending: boolean; onClick: () => void }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton size="small" aria-label={label} disabled={pending} onClick={onClick} sx={ACTION_BUTTON_SX}>
          {pending ? (
            <CircularProgress size={16} />
          ) : label === 'Redo' ? (
            <RedoIcon fontSize="small" />
          ) : (
            <UndoIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function UndoFailure({ error }: { error: unknown }) {
  const refused = (error as { status?: number } | null)?.status === 409;
  return (
    <Typography
      data-testid={refused ? 'lens-change-conflict' : undefined}
      sx={{ order: 1, flexBasis: '100%', fontSize: 12.5, color: refused ? 'text.secondary' : 'error.main', pl: 0.75 }}
    >
      {lensChangeFailure(error)}
    </Typography>
  );
}

function LensUndoButton({ changes }: { changes: AnswerChanges }) {
  const { entry, toggle } = useLensUndo(changes.lensId, changes.ids);
  if (!entry) return null;
  return (
    <>
      {entry.undone ? (
        <Typography sx={{ alignSelf: 'center', color: 'text.secondary', fontSize: 12.5, px: 0.75 }}>
          Undone ·{' '}
          <Link component="button" disabled={toggle.isPending} onClick={() => toggle.mutate()} sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }} underline="hover">
            Redo
          </Link>
        </Typography>
      ) : (
        <UndoIconButton label="Undo" title={undoTitle(changes)} pending={toggle.isPending} onClick={() => toggle.mutate()} />
      )}
      {toggle.error ? <UndoFailure error={toggle.error} /> : null}
    </>
  );
}

function ViewUndoButton({ changes }: { changes: AnswerChanges }) {
  const { standing, undo } = useLensViewUndo(changes.lensId, changes.ids);
  if (!standing) return null;
  return (
    <>
      <UndoIconButton
        label="Undo"
        title={undoTitle(changes)}
        pending={undo.isPending}
        onClick={() => undo.mutate()}
      />
      {undo.error ? <UndoFailure error={undo.error} /> : null}
    </>
  );
}

/** One Undo per answer for every Lens change it made, applied as one operation; on the Lens it then offers Redo. */
export default function AnswerLensUndo({ blocks }: { blocks: ReadonlyArray<LeDataContentBlock> }) {
  return (
    <>
      {answerChanges(blocks).map((changes) =>
        changes.view ? (
          <ViewUndoButton key={changes.lensId} changes={changes} />
        ) : (
          <LensUndoButton key={changes.lensId} changes={changes} />
        ),
      )}
    </>
  );
}
